import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import PropTypes from 'prop-types';
import { shortenAgencyName } from '../../utils/formatters';
import { CHART_COLORS, CHART_TEXT_COLOR } from '../../utils/theme';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * PieChart component for displaying data in a pie chart
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Data array with name and value properties
 * @param {string} [props.title] - Optional chart title
 * @param {boolean} [props.showLegend=true] - Whether to show the legend
 * @param {Object} [props.options] - Additional chart.js options
 * @param {boolean} [props.useMinimalTooltip=false] - Use a minimal tooltip without colored boxes
 * @returns {JSX.Element} Rendered component
 */
const PieChart = ({ 
  data, 
  title, 
  showLegend = true, 
  options = {},
  useMinimalTooltip = false 
}) => {
  // Check if data is valid
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="flex items-center justify-center h-full text-gray-600 font-medium">No data available</div>;
  }

  // Shorten agency names for the chart
  const shortenedData = data.map(item => ({
    name: shortenAgencyName(item.name),
    value: item.value,
    originalName: item.name // Keep original for tooltips
  }));

  const chartData = {
    labels: shortenedData.map(item => item.name),
    datasets: [
      {
        data: shortenedData.map(item => item.value),
        backgroundColor: CHART_COLORS.slice(0, shortenedData.length),
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: !!title,
        text: title || '',
        font: {
          size: 14,
          weight: 'bold'
        },
        color: CHART_TEXT_COLOR,
        padding: {
          top: 10,
          bottom: 20
        }
      },
      legend: {
        display: showLegend,
        position: 'top',
        align: 'start',
        labels: {
          boxWidth: 15,
          boxHeight: 15,
          padding: 15,
          color: CHART_TEXT_COLOR,
          font: {
            size: 12,
            weight: 'bold'
          },
          // Increase line spacing
          generateLabels: function(chart) {
            const defaultLabels = ChartJS.overrides.pie.plugins.legend.labels.generateLabels(chart);
            return defaultLabels.map(label => {
              return {
                ...label,
                // Make every label a bit taller with padding
                padding: 10,
                lineHeight: 1.5
              };
            });
          }
        },
        // Add more space between legend items
        maxHeight: 300,
        // Improve legend layout
        maxWidth: 1000,
        fullSize: true
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const item = shortenedData[context.dataIndex];
            const value = item.value;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            // Show original full name in tooltip
            return `${item.originalName}: $${value.toLocaleString()} (${percentage}%)`;
          },
          // For minimal tooltip, hide the colored box
          labelPointStyle: function() {
            return useMinimalTooltip ? { pointStyle: null } : undefined;
          },
          labelTextColor: function() {
            return '#fff';
          }
        },
        displayColors: !useMinimalTooltip, // Don't show color boxes in tooltip
        boxPadding: useMinimalTooltip ? 0 : 6,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        // Make tooltip wider
        bodySpacing: 6,
        boxWidth: useMinimalTooltip ? 0 : 12,
        boxHeight: useMinimalTooltip ? 0 : 12
      }
    },
    // Custom layout options to make the chart smaller, giving more room for legend
    layout: {
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    }
  };

  // Merge custom options if provided
  const mergedOptions = {
    ...chartOptions,
    ...options,
    plugins: {
      ...chartOptions.plugins,
      ...(options.plugins || {}),
      // Ensure tooltip overrides are applied
      tooltip: {
        ...chartOptions.plugins.tooltip,
        ...(options.plugins?.tooltip || {}),
        displayColors: !useMinimalTooltip,
        boxWidth: useMinimalTooltip ? 0 : (options.plugins?.tooltip?.boxWidth || chartOptions.plugins.tooltip.boxWidth)
      }
    }
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <Pie data={chartData} options={mergedOptions} />
    </div>
  );
};

PieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    })
  ).isRequired,
  title: PropTypes.string,
  showLegend: PropTypes.bool,
  options: PropTypes.object,
  useMinimalTooltip: PropTypes.bool
};

export default PieChart;